import React, {useState, useEffect} from "react";
import { DEFAULT_PREP_TIMES } from "../../constants/payeeCatalog";
const clamp = (value, min, max) => Math.max(min, Math.min(value, max));
const timeToMins = (str) => {
  const [h, m] = str.split(":").map(Number);
  return h * 60 + m;
};

const minsToTime = (mins) => {
  const h = String(Math.floor(mins / 60)).padStart(2, "0");
  const m = String(mins % 60).padStart(2, "0");
  return `${h}:${m}`;
};

const PrepTimeSlider = ({
  prepTimes,
  onChange = () => {},
  title = "",
  readyLabel = "Ready in",
}) => {
  const resolved = prepTimes && prepTimes.avg && prepTimes.buffer && prepTimes.max ? prepTimes : DEFAULT_PREP_TIMES;
  const { avg: avgString, buffer: bufferString, max: maxString } = resolved;

  const avg = timeToMins(avgString);
  const buffer = timeToMins(bufferString);
  const max = timeToMins(maxString);

  useEffect(() => {
    if (!prepTimes || !prepTimes.avg || !prepTimes.buffer || !prepTimes.max) {
      onChange(DEFAULT_PREP_TIMES)
    }
  }, [])

  //highlight errors
  const [errorHighlight, setErrorHighlight] = useState("")
  const highlightError = (highlight) => {
      setErrorHighlight(highlight)
      setTimeout(() => setErrorHighlight(""), 1000)
  }

  const avgPlusBuffer = avg + buffer;

  const handleAvgChange = (e) => {
    const newAvg = parseInt(e.target.value) || 0;
    if (newAvg + buffer <= max) {
      onChange({ avg: minsToTime(newAvg), buffer: bufferString, max: maxString });
    }
  };

  const handleBufferChange = (e) => {
    const inputValue = parseInt(e.target.value) || 0;
    const newBuffer = clamp(inputValue - avg, 0, max - avg);
    if (inputValue === avg) {
      highlightError("avg")    
    } 
    else if (inputValue === max) {
      highlightError("max")
    }
    else if (avg + newBuffer <= max) {
      onChange({ avg: avgString, buffer: minsToTime(newBuffer), max: maxString });
    }
  };

  const handleMaxChange = (e) => {
    const newMax = clamp(parseInt(e.target.value) || 0, avg + buffer, 240);
    onChange({ avg: avgString, buffer: bufferString, max: minsToTime(newMax) });
  };

  
  return (
    <div className="card max-width-100vw margin-auto">
      {title ? <div className="font-size-16px font-weight-600 margin-bottom-05em">{title}</div> : null}

      <div className="display-flex gap-2 margin-bottom-05em">
        <div className={`flex-1 ${errorHighlight === "avg" ? "errorShake" : ""}`}>
          <label className="font-size-12px font-weight-500 display-block margin-bottom-5px">Usual time (avg)</label>
          <input
            type="number"
            value={avg}
            onChange={handleAvgChange}
            className="width-100pct padding-05em border-width-1px border-radius-4px"
            min={1}
            max={180}
          />
        </div>
        <div className="flex-1">
          <label className="font-size-12px font-weight-500 display-block margin-bottom-5px">Upper limit</label>
          <input
            type="number"
            value={avgPlusBuffer}
            onChange={handleBufferChange}
            className="width-100pct padding-05em border-width-1px border-radius-4px"
            min={avg}
            max={max}
          />
        </div>
        <div className={`flex-1 ${errorHighlight === "max" ? "errorShake" : ""}`}>
          <label className="font-size-12px font-weight-500 display-block margin-bottom-5px">Worst case</label>
          <input
            type="number"
            value={max}
            onChange={handleMaxChange}
            className="width-100pct padding-05em border-width-1px border-radius-4px"
            min={avg + buffer}
            max={240}
          />
        </div>
      </div>

      <div
        className="position-relative height-100pct background-color-light-grey border-radius-4px"
        style={{ height: "12px", display: "flex" }}
      >
        <div
          style={{
            width: `${(avg / max) * 100}%`,
            backgroundColor: "var(--success-green)",
            height: "100%",
          }}
        />
        <div
          style={{
            width: `${(buffer / max) * 100}%`,
            backgroundColor: "var(--warning-yellow)",
            height: "100%",
          }}
        />
        <div
          style={{
            width: `${((max - avg - buffer) / max) * 100}%`,
            backgroundColor: "var(--dark-error-red)",
            height: "100%",
            borderRadius: "0 4px 4px 0",
          }}
        />
      </div>
      {/* Tick markers */}
      <div
        style={{
          position: "relative",
          marginTop: "4px",
          height: "16px",
        }}
      >
        {/* 0 */}
        <div style={{ position: "absolute", left: 0, fontSize: "10px" }}>0</div>

        {/* avg */}
        <div
          style={{
            position: "absolute",
            left: `${(avg / max) * 100}%`,
            transform: "translateX(-50%)",
            fontSize: "10px",
            color: "var(--dark-success-green)",
            fontWeight: 600,
          }}
        >
          {avg}
        </div>

        {/* avg + buffer */}
        <div
          style={{
            position: "absolute",
            left: `${(avgPlusBuffer / max) * 100}%`,
            transform: "translateX(-50%)",
            fontSize: "10px",
            color: "var(--dark-warning-yellow)",
            fontWeight: 600,
          }}
        >
          {avgPlusBuffer}
        </div>

        {/* (late) */}
        {(max - avgPlusBuffer >= 15) && (
          <div
            style={{
              position: "absolute",
              left: `${((avgPlusBuffer + (max - avgPlusBuffer) / 2) / max) * 100}%`,
              transform: "translateX(-50%)",
              fontSize: "10px",
              // fontStyle: "italic",
              color: "#666",
              whiteSpace: "nowrap",
              color: "var(--dark-error-red)"
            }}
          >
            (late)
          </div>
        )}

        {/* max */}
        <div
          style={{
            position: "absolute",
            right: 0,
            fontSize: "10px",
            textAlign: "right",
            color: "var(--dark-error-red)",
          }}
        >
          {max}
        </div>
      </div> 
      <div className="font-size-14px margin-top-5px">
        <div>
          {readyLabel}: <span className="font-weight-600">{avg} - {avgPlusBuffer} mins</span>
        </div>
        <div>
          Cancel after: <span className="font-weight-600">{max} mins</span>
        </div>
      </div>
    </div>
  );
};

export default PrepTimeSlider;
